import { ClientEndpointMS2Version, ClientEndpointMS2ReportingResolution, ClientEndpointMS2ScheduleMode, getClientEndpointMS2ReportingResolutionDisplayName, getClientEndpointMS2ScheduleModeDisplayName, ClientEndpointType, MS2ClientEndpoint } from "@app/shared";
import { useModel } from "@app/shared/src/hooks";
import { CheckboxInput, EnumSelect, Input, InputRow, NumericInput } from "@app/dashboard/src/components/shared/inputs";
import { useHref } from "react-router-dom";
import { apiDocsHrefForEndpoint } from "../EndpointApiDocs/specRegistry";
import { ClientEndpointEditorProps } from "./ClientEndpointEditor";

type EditMS2ClientEndpointProps = {} & ClientEndpointEditorProps;

export const EditMS2ClientEndpoint: React.FC<EditMS2ClientEndpointProps> = (props) => {
    const endpointWithDefaults = !!!props.endpoint?.id ? { ...props.endpoint, jsonVersion: 2 } : props.endpoint;
    const [endpoint, helper] = useModel(MS2ClientEndpoint, endpointWithDefaults as MS2ClientEndpoint);
    const onChange = (newEndpoint: Partial<MS2ClientEndpoint>) => {
        props.onChange({ ...newEndpoint as MS2ClientEndpoint, endpointType: ClientEndpointType.MS2 });
    };
    const appBaseHref = useHref("/");
    const docsHref = apiDocsHrefForEndpoint(
        { endpointType: ClientEndpointType.MS2 },
        { docsOrigin: props.docsOrigin, hostsDocsPage: !props.offlineMode, appBaseHref }
    );
    return (
        <>
            {docsHref && (
                <InputRow>
                    <a href={docsHref} target="_blank" rel="noopener noreferrer" className="text-link-page underline text-sm">
                        View the MS2 Traffic Data Feed documentation (setup fields, lane-line workflow, reconciliation) ↗
                    </a>
                </InputRow>
            )}
            <InputRow>
                <Input label="Endpoint Name" {...helper.bindingsFor("endpointName", onChange)} />
            </InputRow>
            <InputRow>
                <Input label="Endpoint URL" {...helper.bindingsFor("endpointUrl", onChange)} />
            </InputRow>
            <InputRow>
                <Input label="MS2 Username" {...helper.bindingsFor("userName", onChange)} />
            </InputRow>
            <InputRow>
                <Input label="MS2 Original File Name" {...helper.bindingsFor("originalFileName", onChange)} />
            </InputRow>
            <InputRow>
                <Input label="Api Key" placeholder="Api Key" {...helper.bindingsFor("sharedSecretKey", onChange)} />
            </InputRow>
            <InputRow>
                <Input label="Custom Auth Header" placeholder="MY_AUTH_HEADER=authValue" {...helper.bindingsFor("customAuthHeader", onChange)} />
            </InputRow>
            <InputRow>
                <Input label="MS2 Location ID" placeholder="e.g., W763, Y754" {...helper.bindingsFor("mS2LocationId", onChange)} />
            </InputRow>
            <InputRow>
                <EnumSelect enumType={ClientEndpointMS2Version} label="MS2 Version" emptyOptionText="Select version" {...helper.bindingsFor("mS2Version", onChange)} value={endpoint.mS2Version ?? ClientEndpointMS2Version.V1_8} />
            </InputRow>
            <InputRow>
                <EnumSelect enumType={ClientEndpointMS2ReportingResolution} label="Reporting Resolution" nameToDisplayConverter={getClientEndpointMS2ReportingResolutionDisplayName} {...helper.bindingsFor("reportingResolution", onChange)} value={endpoint.reportingResolution ?? ClientEndpointMS2ReportingResolution.Directional} />
            </InputRow>
            <InputRow>
                <div className="text-sm text-gray-500 ml-1 -mt-2 mb-2">
                    How each vehicle's movement is reported — pick one: the direction code (NB/SB/EB/WB) or the lane number, not both.
                </div>
            </InputRow>
            <InputRow>
                <CheckboxInput label="Enable Heartbeat" {...helper.bindingsFor("enableHeartbeat", onChange)} />
            </InputRow>
            {endpoint.enableHeartbeat && (
                <>
                    <InputRow>
                        <NumericInput label="Seconds Between Heartbeat" {...helper.bindingsFor("secondsBetweenHearbeat", onChange)} />
                    </InputRow>
                    <InputRow>
                        <div className="text-sm text-gray-500 ml-1 -mt-2 mb-2">
                            Heartbeat will ping <code className="bg-gray-100 px-1 rounded">/Import/v1/tcds/Upload/Ping</code> to verify credentials are valid.
                        </div>
                    </InputRow>
                </>
            )}
            <InputRow>
                <EnumSelect enumType={ClientEndpointMS2ScheduleMode} label="Delivery Schedule" nameToDisplayConverter={getClientEndpointMS2ScheduleModeDisplayName} {...helper.bindingsFor("scheduleMode", onChange)} value={endpoint.scheduleMode ?? ClientEndpointMS2ScheduleMode.Direct} />
            </InputRow>
            <InputRow>
                <div className="text-sm text-gray-500 ml-1 -mt-2 mb-2">
                    "Device sends directly" uploads to MS2 from the device itself (current behavior). The other options have the device hand records to OmniSight, and OmniSight uploads them every few minutes or once a day — better for daily files and if the device goes offline.
                </div>
            </InputRow>
            {endpoint.scheduleMode !== ClientEndpointMS2ScheduleMode.Daily && (
                <>
                    <InputRow>
                        <NumericInput label="Aggregation Interval (minutes)" {...helper.bindingsFor("aggregationIntervalMinutes", onChange)} />
                    </InputRow>
                    <InputRow>
                        <div className="text-sm text-gray-500 ml-1 -mt-2 mb-2">
                            When the device sends directly: 0 = real-time, &gt;0 = the device batches and uploads every N minutes. When OmniSight sends every few minutes: how often OmniSight uploads (1–60 min). Not used for the once-a-day option.
                        </div>
                    </InputRow>
                </>
            )}
        </>
    );
}
